import React, { useState, useRef, useEffect } from "react";
import About from "./About";
import { Link } from "react-router-dom";
import FirstPage from "./firstPage";
import Logo from "../assets/images/nutrilens-logo.png";
import Features from "./Features";
import Screenshot from "./screenshot";
import Download from "./download";
import bg2 from "../assets/images/bg2.png";
import HelpCenter from './HelpCenter';
import Footer from "./Footer";
import Contact from "./contact";
import { IoMenu, IoClose } from "react-icons/io5";

const Home = () => {
  const aboutRef = useRef(null);
  const featuresRef = useRef(null);
  const screenshotsRef = useRef(null);
  const contactUsRef = useRef(null);
  const sidebarRef = useRef(null);
  const headerRef = useRef(null);

  const [showSidebar, setShowSidebar] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const scrollToSection = (sectionRef) => {
    if (sectionRef.current) {
      const largeScreenOffset = 5;
      const mobileScreenOffset = 90;
      const offset = isLargeScreen ? largeScreenOffset : mobileScreenOffset;

      const sectionTop = sectionRef.current.offsetTop - offset;

      window.scrollTo({
        top: sectionTop,
        behavior: "smooth",
      });

      setShowSidebar(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScroll = () => {
    if (headerRef.current) {
      if (window.scrollY > 200) {
        headerRef.current.classList.add("shadow-lg");
      } else {
        headerRef.current.classList.remove("shadow-lg");
      }
    }

    if (window.scrollY > 200) {
      setShowScrollToTop(true);
    } else {
      setShowScrollToTop(false);
    }
  };

  const handleClickOutside = (event) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      setShowSidebar(false);
    }
  };

  useEffect(() => {
    if (showSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSidebar]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsLargeScreen(width >= 768); // Adjusted condition for large screens
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="px-8 lg:px-32 xl:px-52 bg-white ">
        <header
          ref={headerRef}
          className={`text-black z-10 bg-white ${
            isLargeScreen ? "" : "fixed top-0 left-0 right-0 w-full"
          }`}
          style={{ width: "100%" }}
        >
          <div className="container mx-auto flex items-center justify-between h-[75px] px-8 md:px-0">
            <div
              className="flex items-center gap-2.5 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <img
                src={Logo}
                alt="NutriLens AI logo"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl object-cover shrink-0"
              />
              <span className="text-lg sm:text-xl font-extrabold text-[#12AD63] whitespace-nowrap leading-none">
                NutriLens <span className="text-[#2b2b2b]">AI</span>
              </span>
            </div>

            <div className="md:hidden flex items-center absolute top-1 right-0 py-4 px-8 z-10">
              <button onClick={() => setShowSidebar(true)}>
                <IoMenu size={30} className="text-black" />
              </button>
            </div>
            <nav className="hidden md:flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6 font-semibold">
              <button
                onClick={() => scrollToSection(aboutRef)}
                className="hover:text-[#12AD63] text-sm sm:text-base md:text-sm lg:text-base"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection(featuresRef)}
                className="hover:text-[#12AD63] text-sm sm:text-base md:text-sm lg:text-base"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection(screenshotsRef)}
                className="hover:text-[#12AD63] text-sm sm:text-base md:text-sm lg:text-base"
              >
                Screenshots
              </button>
              <button
                onClick={() => scrollToSection(contactUsRef)}
                className="hover:text-[#12AD63] z-10 text-sm sm:text-base md:text-sm lg:text-base"
              >
                Contact Us
              </button>
              <Link
                to="/privacy-policy"
                className="hover:text-[#12AD63] z-10 text-sm sm:text-base md:text-sm lg:text-base"
              >
                Privacy Policy
              </Link>
            </nav>
          </div>
        </header>

        {showSidebar && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-50">
            <div
              ref={sidebarRef}
              className="fixed top-0 right-0 h-full bg-white shadow-lg p-4"
              style={{ width: "300px" }}
            >
              <button
                onClick={() => setShowSidebar(false)}
                className="absolute top-4 right-4"
              >
                <IoClose size={30} className="text-black" />
              </button>

              {/* Sidebar Menu */}
              <nav className="flex flex-col space-y-4 mt-10">
                <button
                  className="text-lg font-semibold hover:text-[#12AD63] text-left border-b border-gray-300 pb-2"
                  onClick={() => scrollToSection(aboutRef)}
                >
                  About
                </button>
                <button
                  className="text-lg font-semibold hover:text-[#12AD63] text-left border-b border-gray-300 pb-2"
                  onClick={() => scrollToSection(featuresRef)}
                >
                  Features
                </button>
                <button
                  className="text-lg font-semibold hover:text-[#12AD63] text-left border-b border-gray-300 pb-2"
                  onClick={() => scrollToSection(screenshotsRef)}
                >
                  Screenshots
                </button>
                <button
                  className="text-lg font-semibold hover:text-[#12AD63] text-left border-b border-gray-300 pb-2"
                  onClick={() => scrollToSection(contactUsRef)}
                >
                  Contact Us
                </button>
                <Link
                  to="/privacy-policy"
                  className="text-lg font-semibold hover:text-[#12AD63] text-left border-b border-gray-300 pb-2"
                  onClick={() => setShowSidebar(false)}
                >
                  Privacy Policy
                </Link>
              </nav>
            </div>
          </div>
        )}
        <FirstPage />
      </div>

      <div ref={aboutRef} className="bg-[#eeeeee] -mt-[65px] about-class ">
        <About />
      </div>
      <div ref={featuresRef} id="features">
        <Features />
      </div>
      <div ref={screenshotsRef} id="screenshots" className="bg-[#eeeeee]">
        <Screenshot />
      </div>
      <div
        className="bg-cover bg-center"
        style={{ backgroundImage: `url(${bg2})` }}
      >
        <HelpCenter contactUsRef={contactUsRef} />
        <Download />
      </div>
      <div ref={contactUsRef} id="contact">
        <Contact />
      </div>
      <div className="bg-[#eeeeee]">
        <Footer />
      </div>

      {/* Scroll to Top */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-5 right-5 bg-gray-800 text-white px-3 py-2 rounded-full"
        >
          ↑
        </button>
      )}
    </>
  );
};

export default Home;
